/**
 * Page 3 of the booking flow, the part with no screen (batch-29, item R11; plan §6): which of the event's add-ons the
 * page lists, what a guest may pick of each, and the `extras` part of the `booking` object. Nothing here works out
 * money: every figure the guest pays is the server's. The server counts a required add-on for everyone whatever is
 * sent, and brings any number down to the party and the rooms booked; this file keeps the page's counters at the same
 * limits, so what the guest sees picked is what is priced.
 *
 * R27: the server also says which add-ons it charges for everyone whatever is picked (`forced`, migration 0101: required,
 * or per guest and saved before `basis` existed). Such an add-on has nothing to pick, and the selection holds it for the
 * whole booking — every person, every room, or once — which is exactly what the server charges for it. A read without
 * the key (an older database) is read as before: `required` alone, and nothing sent for it.
 */
import { money } from '../../lib/format';
import type { PublicAgeGroup, PublicReservationItem } from '../public/api';
import { isShown } from '../public/roomCharge';
import { groupPrice, itemBasis, type Basis, type Per } from './itemBasis';
import type { DraftExtra } from './state';

type Counts = Record<string, number>;
const whole = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0);

/** The add-ons the page lists, in the event's order: every one the guest is charged for (a room line is left out when rooms are charged from the room prices). */
export function shownExtras(items: readonly PublicReservationItem[] | null | undefined): PublicReservationItem[] {
  return (items ?? []).filter(isShown);
}

/** Charged for everyone whatever the guest picks: the server's own word where it sends one, else `required` as before. */
export const isForced = (item: PublicReservationItem): boolean => (typeof item.forced === 'boolean' ? item.forced : item.required === true);

/**
 * What the server charges of an add-on it charges for everyone, as a pick: the whole party by age group (per person),
 * every room booked (per room), or once (per booking). Null when the read does not say `forced: true`, and when there
 * is nobody or no room yet to charge it for.
 */
export function heldForAll(item: PublicReservationItem, groups: readonly PublicAgeGroup[], party: Counts, rooms: number): DraftExtra | null {
  if (item.forced !== true) return null;
  const { basis } = itemBasis(item);
  if (basis === 'person') {
    const people: Counts = {};
    for (const g of partyGroups(groups, party)) people[g.key] = whole(party[g.key]);
    return Object.keys(people).length ? { id: item.id, people } : null;
  }
  if (basis === 'room') return rooms > 0 ? { id: item.id, rooms: whole(rooms) } : null;
  return { id: item.id, qty: 1 };
}

/** The small grey line under an add-on's name. */
export function basisText(item: PublicReservationItem): string {
  const { basis, per } = itemBasis(item);
  if (basis === 'booking') return 'per booking';
  return `per ${basis}, per ${per}`;
}

/** The age groups with someone in the party, oldest first: the ones a per-person add-on is counted for. */
export function partyGroups(groups: readonly PublicAgeGroup[], party: Counts): PublicAgeGroup[] {
  return [...groups].reverse().filter((g) => (party[g.key] ?? 0) > 0);
}

/**
 * An add-on's price as the page words it: one figure, or — for a per-person add-on whose price is not the same for
 * every group of this party — a figure per group ("Adults $45.00 · Children $25.00"). A group with no price of its
 * own is shown at the add-on's price.
 */
export function priceText(item: PublicReservationItem, groups: readonly PublicAgeGroup[], party: Counts, currency: string): string {
  const one = (cents: number) => (cents === 0 ? 'Free' : money(cents, currency));
  if (itemBasis(item).basis !== 'person') return one(item.price_cents);
  const of = partyGroups(groups, party).map((g) => ({ name: g.name, cents: groupPrice(item, g.key) }));
  if (!of.length || of.every((x) => x.cents === of[0]!.cents)) return one(of[0]?.cents ?? item.price_cents);
  return of.map((x) => `${x.name} ${one(x.cents)}`).join(' · ');
}

/** The most a guest may take of a per-booking add-on: its own limit, and what is left of it where it runs out. */
export function bookingCap(item: PublicReservationItem): number {
  const most = whole(item.max_quantity) || 99;
  return item.inventory == null ? most : Math.min(most, whole(item.inventory));
}

export interface ExtraCounter {
  /** `person` counters carry the age group's key; the other two have one counter each. */
  key: string; label: string; value: number; cap: number;
}
export interface ExtraView {
  item: PublicReservationItem; basis: Basis; per: Per;
  /** Counted for everyone: nothing to pick. */
  included: boolean;
  counters: ExtraCounter[];
}

/** One add-on as the page shows it for this party and this many rooms. */
export function extraView(item: PublicReservationItem, picked: DraftExtra | undefined, groups: readonly PublicAgeGroup[], party: Counts, rooms: number): ExtraView {
  const { basis, per } = itemBasis(item);
  if (isForced(item)) return { item, basis, per, included: true, counters: [] };
  if (basis === 'person') {
    return {
      item, basis, per, included: false,
      counters: partyGroups(groups, party).map((g) => ({ key: g.key, label: g.name, value: Math.min(whole(picked?.people?.[g.key]), party[g.key]!), cap: party[g.key]! })),
    };
  }
  if (basis === 'room') return { item, basis, per, included: false, counters: [{ key: 'rooms', label: 'How many rooms', value: Math.min(whole(picked?.rooms), rooms), cap: rooms }] };
  const cap = bookingCap(item);
  return { item, basis, per, included: false, counters: [{ key: 'qty', label: 'How many', value: Math.min(whole(picked?.qty), cap), cap }] };
}

/**
 * The guest's picks as the party and the rooms allow them, in the event's order, each with only the key its add-on is
 * charged by and nothing at zero — which is also exactly what the server is asked with. `lowered` names the add-ons a
 * number came down on (the party or the rooms got smaller after it was picked). An add-on the server says it charges
 * for everyone is always in, for the whole booking, picked or not, and is never named as lowered.
 */
export function fitExtras(
  picked: readonly DraftExtra[] | null | undefined, items: readonly PublicReservationItem[] | null | undefined,
  groups: readonly PublicAgeGroup[], party: Counts, rooms: number,
): { extras: DraftExtra[]; lowered: string[]; changed: boolean } {
  const by = new Map((picked ?? []).map((x) => [x.id, x]));
  const extras: DraftExtra[] = [];
  const lowered: string[] = [];
  for (const item of shownExtras(items)) {
    const was = by.get(item.id);
    if (isForced(item)) {
      const held = heldForAll(item, groups, party, rooms);
      if (held) extras.push(held);
      continue;
    }
    if (!was) continue;
    const view = extraView(item, was, groups, party, rooms);
    let less = false;
    if (view.basis === 'person') {
      const people: Counts = {};
      for (const c of view.counters) if (c.value > 0) people[c.key] = c.value;
      less = Object.entries(was.people ?? {}).some(([k, n]) => whole(n) > (people[k] ?? 0));
      if (Object.keys(people).length) extras.push({ id: item.id, people });
    } else {
      const c = view.counters[0]!;
      const asked = whole(view.basis === 'room' ? was.rooms : was.qty);
      less = asked > c.value;
      if (c.value > 0) extras.push(view.basis === 'room' ? { id: item.id, rooms: c.value } : { id: item.id, qty: c.value });
    }
    if (less) lowered.push(item.name);
  }
  return { extras, lowered, changed: JSON.stringify(extras) !== JSON.stringify(picked ?? []) };
}

/** The picks after one counter moved to `value`. */
export function withExtra(picked: readonly DraftExtra[] | null | undefined, view: ExtraView, key: string, value: number): DraftExtra[] {
  const id = view.item.id;
  if (view.included) return [...(picked ?? [])];
  const was = (picked ?? []).find((x) => x.id === id);
  const n = Math.max(0, Math.trunc(value));
  const next: DraftExtra = view.basis === 'person' ? { id, people: { ...(was?.people ?? {}), [key]: n } } : view.basis === 'room' ? { id, rooms: n } : { id, qty: n };
  return [...(picked ?? []).filter((x) => x.id !== id), next];
}
